// Demo Station Management — TypeScript types

export type DemoStationStatus = 'online' | 'offline' | 'updating' | 'error' | 'retiring';

export type DemoStationCategory = 'gaming' | 'other';

export const DEMO_CATEGORY_LABELS: Record<DemoStationCategory, string> = {
  gaming: 'Gaming',
  other:  'Other',
};

export type DemoCommandType =
  | 'ping'
  | 'reboot'
  | 'shutdown'
  | 'screenshot'
  | 'update_windows'
  | 'update_playlist'
  | 'sync_playlist'
  | 'prepare_seed'
  | 'update_hud'
  | 'update_ad'
  | 'refresh_ad'
  | 'update_agent'
  | 'get_logs'
  | 'get_sensors'
  | 'unlock_usb'
  | 'unlock_task_manager'
  | 'unlock_kiosk'
  | 'lock_kiosk'
  | 'admin_mode'
  | 'toggle_hud'
  | 'toggle_video'
  | 'toggle_ad'
  | 'delete_video'
  | 'live_telemetry'
  | 'start_remote'
  | 'stop_remote'
  | 'start_shell'
  | 'stop_shell'
  | 'playlist_control'
  | 'set_hud_opacity'
  | 'retire';

export type DemoCommandStatus = 'pending' | 'acknowledged' | 'running' | 'completed' | 'failed';

export interface DemoLocation {
  id: string;
  name: string;
  shop_code: string;
  address: string | null;
  is_active: boolean;
  sort_order: number;
  wall_config: Record<string, unknown> | null;
  created_at: string;
}

export interface FanSpeed {
  name: string;
  rpm: number;
}

export interface DemoStation {
  id: string;
  machine_id: string;
  name: string;
  location_id: string | null;
  build_id: string | null;
  status: DemoStationStatus;
  last_seen: string | null;
  agent_version: string | null;
  ip_address: string | null;
  hostname: string | null;
  os_version: string | null;
  os_build: string | null;
  pending_updates: number;
  pending_update_titles: string[] | null;
  cpu_usage: number | null;
  ram_usage: number | null;
  ram_used_mb: number | null;
  ram_total_mb: number | null;
  disk_usage: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
  uptime_seconds: number | null;
  serial_number: string | null;
  // Phase 1 telemetry
  cpu_temp: number | null;
  gpu_temp: number | null;
  gpu_usage: number | null;
  gpu_memory_used_mb: number | null;
  gpu_memory_total_mb: number | null;
  gpu_power_w: number | null;
  gpu_hotspot_temp: number | null;
  fan_speeds: FanSpeed[] | null;
  network_speed_mbps: number | null;
  network_bandwidth_mbps: number | null;
  // Phase 2 screenshot
  screenshot_url: string | null;
  screenshot_at: string | null;
  // Display detection
  has_display: boolean | null;
  hud_enabled: boolean;
  hud_window_visible: boolean | null;
  hud_bg_opacity: number | null;
  pre_demo_hud_enabled: boolean | null;
  /** Overlay states captured on entering demo mode; null when not in it. */
  pre_demo_video_enabled: boolean | null;
  pre_demo_ad_enabled: boolean | null;
  video_enabled: boolean;
  ad_enabled: boolean;
  ad_window_visible: boolean | null;
  /**
   * True while the kiosk shell is suspended - Explorer running, watchdog off.
   * Implied by either mode below; on its own it no longer means Demo Mode.
   */
  kiosk_suspended: boolean;
  /** True while the overlays are hidden for a customer. Independent of `admin_mode`. */
  demo_mode: boolean;
  /** True only while Admin Mode is lifting the elevated policies and USB block. */
  admin_mode: boolean;
  /**
   * Station policy, not current state: true on machines where Admin Mode must
   * never auto-relock (workshop benches). Showroom stations leave it false and
   * keep the timed unlock.
   */
  admin_mode_persistent: boolean;
  /** When the current Admin Mode unlock began; null when `admin_mode` is false. */
  admin_mode_since: string | null;
  /**
   * When the current standalone USB unlock expires; null when no timed unlock is
   * running. Null does not mean USB is blocked while `admin_mode` is true —
   * Admin Mode holds USB open for as long as it lasts, with no deadline of its
   * own on a persistent station.
   */
  usb_unlocked_until: string | null;
  playback_state: {
    video_id: string;
    video_name: string;
    index: number;
    total: number;
    progress_pct: number;
    current_seconds: number;
    duration_seconds: number;
    paused: boolean;
    volume: number;
  } | null;
  retire_phase: string | null;
  notes: string | null;
  category: DemoStationCategory;
  created_at: string;
  updated_at: string;
}

export interface DemoStationWithDetails extends DemoStation {
  location: DemoLocation | null;
  build_sku: string | null;
  build_description: string | null;
}

export interface DemoCommand {
  id: string;
  station_id: string;
  command_type: DemoCommandType;
  payload: Record<string, unknown>;
  status: DemoCommandStatus;
  result: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
  acknowledged_at: string | null;
  completed_at: string | null;
}

export interface DemoStationLog {
  id: string;
  station_id: string;
  event_type: string;
  details: Record<string, unknown> | null;
  created_at: string;
}

export const DEMO_STATUS_CONFIG: Record<DemoStationStatus, { label: string; color: string; dot: string }> = {
  online:   { label: 'Online',   color: 'text-green-600',  dot: 'bg-green-500' },
  offline:  { label: 'Offline',  color: 'text-gray-400',   dot: 'bg-gray-400' },
  updating: { label: 'Updating', color: 'text-blue-600',   dot: 'bg-blue-500' },
  error:    { label: 'Error',    color: 'text-red-600',    dot: 'bg-red-500' },
  retiring: { label: 'Retiring', color: 'text-orange-600', dot: 'bg-orange-500' },
};

export const DEMO_COMMAND_LABELS: Record<DemoCommandType, string> = {
  ping:                 'Ping',
  reboot:               'Reboot',
  shutdown:             'Shutdown',
  screenshot:           'Screenshot',
  update_windows:       'Windows Update',
  update_playlist:      'Update Playlist',
  sync_playlist:        'Sync Playlist',
  prepare_seed:         'Prepare Seed',
  update_hud:           'Update HUD',
  update_ad:            'Update Ad',
  refresh_ad:           'Refresh Ad',
  update_agent:         'Update Agent',
  get_logs:             'Get Logs',
  get_sensors:          'Get Sensors',
  unlock_usb:           'Unlock USB',
  unlock_task_manager:  'Unlock Task Manager',
  unlock_kiosk:         'Unlock Kiosk',
  lock_kiosk:           'Lock Kiosk',
  admin_mode:           'Admin Mode',
  toggle_hud:           'Toggle HUD',
  toggle_video:         'Toggle Video',
  toggle_ad:            'Toggle Ad',
  delete_video:         'Delete Video',
  live_telemetry:       'Live Telemetry',
  start_remote:         'Start Remote Desktop',
  stop_remote:          'Stop Remote Desktop',
  start_shell:          'Start Remote Shell',
  stop_shell:           'Stop Remote Shell',
  playlist_control:     'Playlist Control',
  set_hud_opacity:      'Set HUD Opacity',
  retire:               'Retire Station',
};

// ── Ad Campaign Types ─────────────────────────────────────────────────────────

export interface AdAsset {
  id: string;
  name: string;
  type: 'image' | 'html';
  image_url: string | null;
  storage_path: string | null;
  html: string | null;
  created_by: string | null;
  created_at: string;
}

export type AdTargetType = 'all' | 'location' | 'station' | 'build' | 'family';

export interface Targeting {
  locations?: string[];  // location UUIDs
  families?: string[];   // family codes (E, L, M, P, 1, Z)
  builds?: string[];     // build UUIDs
  stations?: string[];   // station UUIDs
}

export const PRODUCT_FAMILIES: Record<string, string> = {
  'E': 'Elite',
  'L': 'Legend',
  'M': 'Master',
  'P': 'Pro',
  '1': 'One',
  'Z': 'Zero',
};

export function getFamilyFromSku(sku: string | null | undefined): string | null {
  if (!sku) return null;
  const prefix = sku.replace(/^AOI-/, '').charAt(0);
  return prefix in PRODUCT_FAMILIES ? prefix : null;
}

export interface CampaignPlaylistItem {
  id: string;
  campaign_id: string;
  asset_id: string;
  sort_order: number;
  duration_seconds: number;
  created_at: string;
  asset?: AdAsset;
}

export interface AdCampaign {
  id: string;
  name: string;
  asset_id: string | null;
  target_type: AdTargetType;
  target_id: string | null;
  targeting: Targeting;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  priority: number;
  display_duration_seconds: number | null;
  display_interval_seconds: number | null;
  show_from: string | null;
  show_until: string | null;
  created_by: string | null;
  created_at: string;
  // joined
  asset?: AdAsset;
  playlist_items?: CampaignPlaylistItem[];
}

// ── Video Playlist Types ──────────────────────────────────────────────────────

export interface Video {
  id: string;
  name: string;
  file_name: string;
  file_size: number | null;
  duration_seconds: number | null;
  storage_url: string | null;
  thumbnail_url: string | null;
  seed_station_id: string | null;
  created_by: string | null;
  created_at: string;
}

export interface VideoPlaylist {
  id: string;
  name: string;
  created_by: string | null;
  created_at: string;
  items?: VideoPlaylistItem[];
}

export interface VideoPlaylistItem {
  id: string;
  playlist_id: string;
  video_id: string;
  sort_order: number;
  is_flagship: boolean;
  created_at: string;
  video?: Video;
}

export interface PlaylistAssignment {
  id: string;
  playlist_id: string;
  target_type: AdTargetType;
  target_id: string | null;
  targeting: Targeting;
  priority: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  playlist?: VideoPlaylist;
}

export interface VideoPeer {
  id: string;
  station_id: string;
  video_id: string;
  status: 'pending' | 'ready_for_upload' | 'downloading' | 'seeding' | 'error';
  progress_pct: number;
  tunnel_url: string | null;
  lan_ip: string | null;
  bytes_downloaded: number | null;
  error_message: string | null;
  updated_at: string;
}

export interface AgentRelease {
  id: string;
  version: string;
  download_url: string;
  release_notes: string | null;
  is_current: boolean;
  published_at: string;
  published_by: string | null;
}
